import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "urql";
import {
  AvailableTimezonesQuery,
  type TimezoneChoices,
} from "@/data-access/news-alerts/queries/timezone-queries";
import { resolveLocalTimezoneValue } from "./timezoneComboboxHelpers";

interface UseAvailableTimezonesOptions {
  timezone: string;
  onTimezoneChange: (timezone: TimezoneChoices) => void;
  autoSelectLocalTimezone?: boolean;
}

const useAvailableTimezones = ({
  timezone,
  onTimezoneChange,
  autoSelectLocalTimezone = true,
}: UseAvailableTimezonesOptions) => {
  const [{ data, fetching }] = useQuery({
    query: AvailableTimezonesQuery,
    requestPolicy: "cache-first",
  });
  const didAutoSelectTimezone = useRef(false);

  const { availableTimezones, localTimezoneValue } = useMemo(() => {
    const timezones = (data?.availableTimezones ?? []).flatMap((item) =>
      item?.value && item?.label
        ? [{ value: item.value, label: item.label }]
        : [],
    );
    const localValue = resolveLocalTimezoneValue(timezones);
    if (!localValue) {
      return { availableTimezones: timezones, localTimezoneValue: undefined };
    }

    return {
      availableTimezones: [
        ...timezones.filter((item) => item.value === localValue),
        ...timezones.filter((item) => item.value !== localValue),
      ],
      localTimezoneValue: localValue,
    };
  }, [data?.availableTimezones]);

  useEffect(() => {
    if (!autoSelectLocalTimezone) {
      didAutoSelectTimezone.current = false;
      return;
    }
    if (availableTimezones.length === 0 || didAutoSelectTimezone.current) {
      return;
    }

    didAutoSelectTimezone.current = true;
    if (!timezone && localTimezoneValue) {
      onTimezoneChange(localTimezoneValue as TimezoneChoices);
    }
  }, [
    autoSelectLocalTimezone,
    availableTimezones.length,
    localTimezoneValue,
    onTimezoneChange,
    timezone,
  ]);

  return { availableTimezones, isFetchingAvailableTimezones: fetching };
};

export default useAvailableTimezones;
